import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { PulsingDot } from './PulsingDot'

/**
 * HelpBubble — small `?` icon button used inline next to feature labels,
 * fields, or section headers to open the contextual help popover/sheet.
 *
 * Pencil: Vzk9b/nYOmw/ByRUR (HelpBubble States) — five states:
 *   - Default            transparent fill + ia-border-strong stroke
 *   - Hover              ia-surface-hover fill + ia-text-tertiary stroke
 *   - WithPulse          default + PulsingDot at top-right
 *   - Active / Open      ia-brand-glow ring + ia-green-filled inner bubble
 *   - Disabled           opacity 0.55, ia-text-disabled stroke, no hover
 *
 * Purely visual. Behavior (open popover, mark-seen, prefetch) is wired by
 * the caller, typically inside `<HelpBubblePopover>` or
 * `<InlineHelpSheet>` molecules.
 */
export const helpBubbleVariants = cva(
  // base — 18×18 circle, centered glyph, focus ring inherits ia-ring
  [
    'inline-flex items-center justify-center rounded-pill shrink-0',
    'h-[18px] w-[18px] text-[11px] font-semibold leading-none',
    'cursor-pointer outline-none transition-colors',
    'focus-visible:ring-2 focus-visible:ring-ia-green focus-visible:ring-offset-1',
    'focus-visible:ring-offset-card',
    'disabled:cursor-not-allowed disabled:opacity-[0.55]',
  ],
  {
    variants: {
      state: {
        default: [
          'border border-ia-border-strong text-ia-text-tertiary bg-transparent',
          'hover:bg-ia-surface-hover hover:border-ia-text-tertiary',
        ],
        active: [
          'border-0 bg-ia-green text-ia-text-on-brand',
          // Outer glow ring via offset shadow keeps layout from shifting.
          'shadow-[0_0_0_3px_var(--color-ia-brand-glow)]',
        ],
        disabled: ['border border-ia-text-disabled text-ia-text-disabled bg-transparent'],
      },
    },
    defaultVariants: { state: 'default' },
  },
)

export interface HelpBubbleProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>,
    VariantProps<typeof helpBubbleVariants> {
  /** The inline-doc topic this bubble opens. Used for aria-label fallback. */
  topicId: string
  /** Topic title for the aria-label. Falls back to topicId when omitted. */
  topicTitle?: string
  /** Show the pulsing-dot "unseen / updated" indicator. */
  pulsing?: boolean
  /** Render in the active (popover/sheet open) state. */
  active?: boolean
}

/**
 * Compose `state` from the high-level booleans so the consumer only deals
 * in semantic props (pulsing / active / disabled). cva still owns the
 * visual map; pulsing is rendered as an absolute-positioned PulsingDot
 * regardless of state so "unseen + active" can coexist visually (the
 * dot fades out via markSeen on the same click that opened the popover).
 */
function deriveState(args: {
  active?: boolean
  disabled?: boolean
}): VariantProps<typeof helpBubbleVariants>['state'] {
  if (args.disabled) return 'disabled'
  if (args.active) return 'active'
  return 'default'
}

export const HelpBubble = React.forwardRef<HTMLButtonElement, HelpBubbleProps>(
  ({ className, topicId, topicTitle, pulsing, active, disabled, ...props }, ref) => {
    const ariaLabel = topicTitle ? `Help: ${topicTitle}` : `Help: ${topicId}`
    const state = deriveState({ active, disabled })
    return (
      <span className="relative inline-flex">
        <button
          ref={ref}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={active}
          data-topic-id={topicId}
          disabled={disabled}
          className={cn(helpBubbleVariants({ state }), className)}
          {...props}
        >
          ?
        </button>
        {pulsing && !disabled && (
          <PulsingDot
            tone="green"
            size="sm"
            label="Unseen help"
            className="absolute -top-[2px] -right-[2px] pointer-events-none"
          />
        )}
      </span>
    )
  },
)
HelpBubble.displayName = 'HelpBubble'
